// The Marketing chat beside any page but /chat. The top bar's Marketing button opens it; nothing
// floats over a page while it is closed, so no page or sticky bar reserves room for it. All
// conversation behaviour lives in the shared ChatSurface (also the full-screen /chat page).

import { useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { MessageSquare } from 'lucide-react'
import { ChatSurface } from '@/components/chat/ChatSurface'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useUiStore, selectIsChatPanelOpen } from '@/stores/useUiStore'

/** The page top bar's way into the panel: an icon from md, labelled from lg. A phone reaches
 * Marketing from home (every page's back chevron) instead. */
export function ChatPanelButton() {
  const setChatPanelOpen = useUiStore((state) => state.setChatPanelOpen)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="sm"
          className="hidden h-9 rounded-full max-lg:w-9 max-lg:px-0 md:ml-1 md:inline-flex"
          onClick={() => setChatPanelOpen(true)}
          aria-label="Open Marketing chat"
          data-testid="chat-panel-open"
        >
          <MessageSquare className="size-4" />
          <span className="hidden lg:inline">Marketing</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="lg:hidden">
        Marketing
      </TooltipContent>
    </Tooltip>
  )
}

/** Mounts the panel while it is open: over the page's right edge, with a backdrop below xl.
 * It stands down on /chat, whose page is the same surface (mounting both would double-subscribe
 * the streaming and resume hooks). */
export function ChatPanel() {
  const isOpen = useUiStore(selectIsChatPanelOpen)
  const setChatPanelOpen = useUiStore((state) => state.setChatPanelOpen)
  const { pathname } = useLocation()
  const onChatRoute = pathname === '/chat' || pathname.startsWith('/chat/')
  const close = useCallback(() => setChatPanelOpen(false), [setChatPanelOpen])

  useEffect(() => {
    if (onChatRoute && isOpen) close()
  }, [onChatRoute, isOpen, close])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, close])

  if (onChatRoute || !isOpen) return null

  return (
    <>
      <div
        className="bg-foreground/40 fixed inset-0 z-40 xl:hidden"
        onClick={close}
        aria-hidden="true"
      />
      <aside
        data-testid="chat-panel"
        className="bg-background fixed top-0 right-0 z-50 flex h-dvh min-h-0 w-full max-w-[min(384px,100vw)] flex-col border-l shadow-2xl"
      >
        <ChatSurface variant="panel" onClose={close} />
      </aside>
    </>
  )
}
